import React, { useState } from "react";
import { saveAs } from "file-saver";

// ------------------------
// Helper: Format date nicely
// ------------------------
const formatDate = (d) => {
	if (!d) return "";
	const dateObj = new Date(d);
	if (isNaN(dateObj)) return d;
	return dateObj.toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

// ------------------------
// Lazy-load jsPDF & autoTable once
// ------------------------
let jsPDF, autoTable;
const loadJsPDF = async () => {
	if (!jsPDF || !autoTable) {
		const jsPDFModule = await import("jspdf");
		const autoTableModule = await import("jspdf-autotable");
		jsPDF = jsPDFModule.default;
		autoTable = autoTableModule.default;
	}
	return { jsPDF, autoTable };
};

// ------------------------
// Main ReportExporter component
// ------------------------
const ReportExporter = ({
	title = "Report",
	data = [],
	columns = [],
	filterableFields = {},
	showDate = true,
	showSummary = false,
}) => {
	const [filter, setFilter] = useState({
		start: "",
		end: "",
		...Object.fromEntries(Object.keys(filterableFields).map((k) => [k, ""])),
	});

	// ------------------------
	// Filtered data based on user selections
	// ------------------------
	const filteredData = data.filter((row) => {
		const inDate =
			(!filter.start || new Date(row.date) >= new Date(filter.start)) &&
			(!filter.end || new Date(row.date) <= new Date(filter.end));

		const matchesOthers = Object.keys(filterableFields).every(
			(field) => !filter[field] || row[field] === filter[field]
		);

		return inDate && matchesOthers;
	});

	// ------------------------
	// Export Balance Sheet (summary PDF)
	// ------------------------
	const exportBalanceSheet = async () => {
		if (!filteredData.length) return alert("No data to export");

		// Lazy-load jsPDF + autoTable
		const { jsPDF, autoTable } = await loadJsPDF();

		// Filter for summary table
		const filteredCosts = filteredData.filter((c) => {
			const inDate =
				(!filter.start || new Date(c.date) >= new Date(filter.start)) &&
				(!filter.end || new Date(c.date) <= new Date(filter.end));
			const inCategory = !filter.category || c.category === filter.category;
			const inEmployee = !filter.employee || c.userName === filter.employee;
			return inDate && inCategory && inEmployee;
		});

		if (!filteredCosts.length) return alert("No data to export");

		// Aggregate totals by category
		const categoryTotals = {};
		filteredCosts.forEach((c) => {
			if (!categoryTotals[c.category]) categoryTotals[c.category] = 0;
			categoryTotals[c.category] += c.amount;
		});

		// Prepare table rows
		const tableRows = Object.entries(categoryTotals).map(([category, amount]) => [
			category,
			amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " AED",
		]);

		const total = filteredCosts.reduce((sum, c) => sum + c.amount, 0);

		// Initialize PDF
		const doc = new jsPDF();
		doc.setFontSize(16);
		doc.text("Cost Summary Report", 14, 20);

		// Add table
		autoTable(doc, {
			head: [["Category", "Total Amount"]],
			body: tableRows,
			startY: 30,
			theme: "grid",
		});

		// Add total
		doc.setFontSize(12);
		doc.text(
			`Total Costs: ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED`,
			14,
			40 + tableRows.length * 10
		);

		// Save PDF
		const timestamp = new Date()
			.toISOString()
			.replace(/[:.]/g, "-");
		doc.save(`balance_sheet_${timestamp}.pdf`);
	};

	// ------------------------
	// Export Excel
	// ------------------------
    const exportExcel = async () => {
    	if (!filteredData.length) return alert("No data to export");
    
    	// Lazy-load XLSX only when needed
    	const XLSXModule = await import("xlsx");
    	const XLSX = XLSXModule.default || XLSXModule; // some bundlers put it in .default
    
    	// Create worksheet and workbook
    	const ws = XLSX.utils.json_to_sheet(filteredData);
    	const wb = XLSX.utils.book_new();
    	XLSX.utils.book_append_sheet(wb, ws, title);
    
    	// Write workbook to array buffer
    	const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    	const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    
    	// Save the file
    	const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    	saveAs(blob, `${title}_${timestamp}.xlsx`);
    };

	const today = new Date();
	const todayFormatted =
		today.toLocaleDateString("en-GB", {
			weekday: "long",
			day: "2-digit",
			month: "short",
			year: "numeric",
		}) +
		" " +
		today.toLocaleTimeString("en-GB", {
			hour: "2-digit",
			minute: "2-digit",
		});

	// ------------------------
	// Export full PDF with detailed rows
	// ------------------------
	const exportPDF = async () => {
		if (!filteredData.length) return alert("No data to export");

		// Lazy-load jsPDF + autoTable
		const { jsPDF, autoTable } = await loadJsPDF();

		// Initialize PDF in landscape
		const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

		const topPadding = 80;
		doc.setFontSize(16);
		doc.text(`${title}  -  ${todayFormatted}`, 40, topPadding);

		// Prepare table
		const tableColumn = columns.map((c) => c.label);
		const tableRows = filteredData.map((row) =>
			columns.map((c) => {
				if (c.field === "date") return formatDate(row[c.field]);
				if (c.field === "vat") {
					if (row.total) return (row.total * 0.05).toLocaleString();
					return row.netWorth ? (row.netWorth * 0.05).toLocaleString() : "";
				}
				if (c.field === "netWorth") return row.netWorth ? row.netWorth.toLocaleString() : "";
				if (c.field === "price") return row.price ? row.price.toLocaleString() : "";
				if (c.field === "total") return row.total ? row.total.toLocaleString() : "";
				return row[c.field] || "";
			})
		);

		const tablePadding = 40;
		autoTable(doc, { head: [tableColumn], body: tableRows, startY: tablePadding + topPadding });

		const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
		doc.save(`${title}_${timestamp}.pdf`);
	};

	// ------------------------
	// JSX: Filters + Export Buttons
	// ------------------------
	return (
		<div className="mt-auto p-4 border rounded-md">
			<h2 className="text-lg font-bold mb-2">{title}</h2>

			{/* Filters */}
			<div className="flex flex-wrap gap-2 mb-4">
				{showDate && (
					<>
						<input
							type="date"
							value={filter.start}
							onChange={(e) => setFilter({ ...filter, start: e.target.value })}
							className="px-2 py-1 border rounded"
						/>
						<input
							type="date"
							value={filter.end}
							onChange={(e) => setFilter({ ...filter, end: e.target.value })}
							className="px-2 py-1 border rounded"
						/>
					</>
				)}

				{Object.keys(filterableFields).map((field) => (
					<select
						key={field}
						className="px-2 py-1 border rounded"
						value={filter[field]}
						onChange={(e) => setFilter({ ...filter, [field]: e.target.value })}
					>
						<option value="">All {filterableFields[field]}</option>
						{Array.from(new Set(data.map((row) => row[field]))).map(
							(val) => val && <option key={val} value={val}>{val}</option>
						)}
					</select>
				))}
			</div>

			{/* Export Buttons */}
			<div className="flex gap-2">
				{showSummary && (
					<button
						onClick={exportBalanceSheet}
						className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer"
					>
						Export Summary
					</button>
				)}
				<button
					onClick={exportPDF}
					className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer"
				>
					Export PDF
				</button>
				<button
					onClick={exportExcel}
					className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer"
				>
					Export Excel
				</button>
				<button
					onClick={() =>
						setFilter({
							start: "",
							end: "",
							...Object.fromEntries(Object.keys(filterableFields).map((k) => [k, ""])),
						})
					}
					className="px-4 py-1 bg-gray-500 rounded hover:bg-gray-600 cursor-pointer"
				>
					Clear All
				</button>
			</div>
		</div>
	);
};

export default ReportExporter;
